import React, { useState } from "react";
import { object, string, number, date, InferType } from 'yup';

import apiService from "../../../services/ApiService";
import snakeToPascal from "../../../tools";
import CustomDatePicker from "../Input/reusables/CustomDatePicker";
import dayjs from "dayjs";
import { useSaveIndicator } from "../../../hooks/useSaveIndicator";
import SaveIndicator from "../../SaveIndicator";
import { useEditorStore } from "../../../shared/stores/useEditorStore";
import { useQueryClient } from "@tanstack/react-query";
import { SECTION_DATA_KEY } from "../../../shared/hooks/queries/useSectionLoader";


export default function SensitivityTimeSerieTimingField({ item, field, currentSensitivity }) {
    const isReadOnly = !useEditorStore(s => s.userRights.right_to_write);
    const scenarioId = useEditorStore(s => s.scenarioId) ?? 0;
    const queryClient = useQueryClient();

    const invalidateQueries = () => {
        // Only invalidate section-data cache — do NOT invalidate SCENARIO_TREE_KEY (see useUpdateFieldValue.ts)
        queryClient.invalidateQueries({ queryKey: [SECTION_DATA_KEY, scenarioId] });
    };

    const sensitivityField = item.sensitivityModelData.fields[field.name];

    const initialTiming = (sensitivityField) ? sensitivityField.value.value : field.currentValue.value;

    const [timing, setTiming] = React.useState(initialTiming);
    const { status: saveStatus, trigger: triggerSave, resolve: resolveSave } = useSaveIndicator();

    timing.start_date.value = (timing.start_date.value) ? dayjs(timing.start_date.value) : null;
    timing.end_date.value = (timing.end_date.value) ? dayjs(timing.end_date.value) : null;
    timing.periodicity.value = (timing.periodicity.value) ? timing.periodicity.value : 'null';

    const resetSensitivityValue = () => {
        apiService.deleteSensitivityValue(sensitivityField.id).then(
            async(response) => {
                if (response) {
                    delete item.sensitivityModelData.fields[field.name];
                    setTiming(field.currentValue.value);
                    invalidateQueries();
                    // reloadState();
                }
            }
        )
        
    }


    const checkComplete = (timing) => {
        if (timing.start_date.value && timing.end_date.value) {
            if (timing.start_date.value.diff(timing.end_date.value, 'day') >= 0) {
                field.complete = false;
                field.timingError = 'Start date must be before End date.'
            }
            else if (timing.start_date.value.diff(timing.end_date.value, 'month') == 0) {
                field.complete = false;
                field.timingError = 'Difference Between Dates should be at least one month.'
            }
            else if (timing.periodicity.value !== 'null') {
                field.complete = true;
                field.timingError = null;
            }
            else {
                field.timingError = null;
                field.complete = false;
            }
        }
        else {
            field.complete = false;
            field.timingError = null;
        }
    }

    const formatNewTiming = (response) => {
        return {
            start_date: {
                value: response.start_date ? dayjs(response.start_date) : null,
                address: null,
            },
            end_date: {
                value: response.end_date ? dayjs(response.end_date) : null,
                address: null,
            },
            periodicity: {
                value: response.periodicity,
                address: null,
            },
        }
    }


    const handleChangeStartDate = (newValue, timingId) => {
        // Compute iterates at day=1; persisting any other day causes TS/window mismatch.
        const normalized = newValue.date(1);

        triggerSave();
        apiService.putSensitivityTimingValue(timingId,
            normalized.format('YYYY-MM-DD'),
            (timing.end_date.value) ? timing.end_date.value.format('YYYY-MM-DD') : null,
            'monthly',
        ).then(async(response) => {
            if (response) {
                const newTiming = formatNewTiming(await response.json())

                field.start_dateStore.isOpen = false;
                checkComplete(newTiming);

                setTiming(newTiming);
                resolveSave();
                invalidateQueries();
            }
        })

    };

    const handleChangeStartDatewithSensitivity = (newValue) => {
        if (sensitivityField) {
            handleChangeStartDate(newValue, sensitivityField.id);
        } else {
            createSensitivity(newValue, handleChangeStartDate)
        }
    }

    const handleChangeEndDate = (newValue, timingId) => {
        // end_date is normalized to the last day of the month (end of period).
        const normalized = newValue.endOf('month').startOf('day');

        triggerSave();
        apiService.putSensitivityTimingValue(timingId,
            (timing.start_date.value) ? timing.start_date.value.format('YYYY-MM-DD') : null,
            normalized.format('YYYY-MM-DD'),
            'monthly',
        ).then(async(response) => {
            if (response) {
                const newTiming = formatNewTiming(await response.json())
                field.end_dateStore.isOpen = false;
                checkComplete(newTiming);
                setTiming(newTiming);
                resolveSave();
                invalidateQueries();
            }
        })
    };

    const handleChangeEndDatewithSensitivity = (newValue) => {
        if (sensitivityField) {
            handleChangeEndDate(newValue, sensitivityField.id);
        } else {
            createSensitivity(newValue, handleChangeEndDate)
        }
    }

    const createSensitivity = (newValue, handleChangeCallBack) => {
        apiService.upsertSensitivityValue(item.sensitivityModelData.id, field.name, field.type, null).then(
            async(response) => {
                if (response) {
                    const newSensitivityField = await response.json()

                    item.sensitivityModelData.fields[field.name] = newSensitivityField

                    handleChangeCallBack(newValue, newSensitivityField.id);
                }
            }
        )
    }


    return (
        <div className="flex flex-row">
            <div>
                {!isReadOnly && (
                    <button
                        onClick={resetSensitivityValue}
                        disabled={!sensitivityField}
                        className="mr-2 h-10 w-10 flex items-center justify-center text-slate-600 hover:text-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                        <span className="material-symbols-outlined text-[30px]">restart_alt</span>
                    </button>
                )}
            </div>
            <div className="ml-4">
                <div className="mb-3.5">
                    <p className="text-sm text-slate-900 mb-1">
                        Start Date
                    </p>
                    <CustomDatePicker
                        item={field.start_dateStore}
                        currentValue={(timing && timing.start_date.value) ? timing.start_date.value : null}
                        handleChange={handleChangeStartDatewithSensitivity}
                    />
                </div>
                <div className="mb-3.5">
                    <p className="text-sm text-slate-900 mb-1">
                        End Date
                    </p>
                    <CustomDatePicker
                        item={field.end_dateStore}
                        currentValue={(timing && timing.end_date.value) ? timing.end_date.value : null}
                        handleChange={handleChangeEndDatewithSensitivity}
                    />
                </div>
                <SaveIndicator status={saveStatus} className="mt-1" />
                {field.error && (
                    <p className="text-xs text-red-600">
                        {field.error}
                    </p>
                )}
            </div>
        </div>
    )
}
